import { DateTime } from "luxon";
import { isExcludedMatch, orderByDecision } from "./screeningMatch";

/**
 * Deriving the LexisNexis2 Monitoring record view from what the API gives us.
 *
 * The shape of the problem, from TTL-DES-LNX2-003 Section 2.1: the match and its
 * decision are a stable spine, and the results are the moving parts. Each
 * activity attaches results to matches that already exist. Nothing here decides
 * anything; it works out what arrived when, and which of it the officer has not
 * yet signed off.
 *
 * Everything joins on `match.id`, the ScreeningWatchlistMatch primary key, which
 * both the activity payload and the accumulated match list carry.
 */

/** Provider references produced by the LexisNexis2 screening check. */
export const LN2_PROVIDER_REFERENCES = [
  "ln-sanctionscheck2",
  // no hyphens and mixed case. Copied from IndividualMonitoring in the backend
  // model, where it is spelled exactly like this
  "LexisNexiscompanysanctionscheck2",
];

export const isLn2Provider = (providerReference?: string | null) =>
  !!providerReference &&
  LN2_PROVIDER_REFERENCES.includes(String(providerReference));

/** Activity events that carry LexisNexis2 screening results. */
export const SCREENING_EVENT = "Screening";

/**
 * An activity is signed off once it leaves review. `pending` has not run yet and
 * `review` is waiting on the officer; everything else has been dealt with, either
 * automatically or by hand.
 */
export const UNSIGNED_ACTIVITY_STATUSES = ["review", "pending"];

export const isUnsignedActivity = (activity: any) =>
  UNSIGNED_ACTIVITY_STATUSES.includes(activity?.status?.status);

export interface MatchContribution {
  activityId: number;
  activityDate: string | null;
  /** True while the activity that produced it is not signed off. */
  isNew: boolean;
  /** The formatted match body as that activity reported it. */
  body: any;
  /** How many result rows this activity attached to this match. */
  resultCount: number;
}

export interface MonitoringMatch {
  id: number;
  /** Newest contribution's body, the default view. */
  body: any;
  /** Newest first. One per activity that returned this subject. */
  contributions: MatchContribution[];
  resultCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  /** Carries a result from an activity that has not been signed off. */
  hasNewResults: boolean;
  /**
   * Excluded by the tenant's screening preferences, as the newest run reports
   * it. Not counted, and listed apart from the work queue.
   */
  isExcluded: boolean;
  /** Live decision from the accumulated match list, not from any one run. */
  status: string | null;
  statusDisplay: string | null;
}

const toMillis = (value?: string | null) => {
  if (!value) return 0;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toMillis() : 0;
};

export const formatShortDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat("dd MMM yyyy") : "";
};

/**
 * Fold the loaded activity payloads into one match per subject.
 *
 * `activities` supplies each payload's date and sign-off status, which the
 * payload itself does not reliably carry in a form we can sort on.
 */
export const buildMonitoringMatches = (
  payloads: { activityId: number; data: any }[],
  activities: any[],
  accumulatedMatches: any[] = [],
): MonitoringMatch[] => {
  const activityById = new Map<number, any>(
    (activities || []).map((activity) => [activity?.id, activity]),
  );
  const liveStatusById = new Map<number, any>(
    (accumulatedMatches || []).map((match) => [match?.id, match]),
  );

  const byMatchId = new Map<number, MonitoringMatch>();

  for (const payload of payloads) {
    const activity = activityById.get(payload.activityId);
    // LN always returns a single record per run
    const record = payload.data?.instance?.records?.[0];
    const matches = record?.matches || [];

    for (const body of matches) {
      if (body?.id == null) continue;

      const contribution: MatchContribution = {
        activityId: payload.activityId,
        activityDate: activity?.created_on ?? null,
        isNew: isUnsignedActivity(activity),
        body,
        resultCount: body?.results_count ?? (body?.results || []).length ?? 1,
      };

      const existing = byMatchId.get(body.id);
      if (existing) {
        existing.contributions.push(contribution);
      } else {
        byMatchId.set(body.id, {
          id: body.id,
          body,
          contributions: [contribution],
          resultCount: 0,
          firstSeen: null,
          lastSeen: null,
          hasNewResults: false,
          isExcluded: false,
          status: null,
          statusDisplay: null,
        });
      }
    }
  }

  return Array.from(byMatchId.values()).map((match) => {
    const contributions = [...match.contributions].sort(
      (a, b) => toMillis(b.activityDate) - toMillis(a.activityDate),
    );
    const live = liveStatusById.get(match.id);

    return {
      ...match,
      contributions,
      // the newest run is the consolidated current state, and the default the
      // Evidence strip opens on
      body: contributions[0]?.body ?? match.body,
      resultCount: contributions.reduce(
        (total, contribution) => total + contribution.resultCount,
        0,
      ),
      firstSeen: contributions[contributions.length - 1]?.activityDate ?? null,
      lastSeen: contributions[0]?.activityDate ?? null,
      hasNewResults: contributions.some((contribution) => contribution.isNew),
      isExcluded: isExcludedMatch(contributions[0]?.body ?? match.body),
      // the accumulated list is authoritative on the decision; a payload's copy
      // is the same value but the list also covers matches no loaded activity
      // returned
      status: live?.status ?? contributions[0]?.body?.status ?? null,
      statusDisplay:
        live?.status_display ?? contributions[0]?.body?.status_display ?? null,
    };
  });
};

/**
 * The work queue, read top to bottom: True Match >> Unknown >> False Positive,
 * then by LexisNexis score, highest first. The same rule as the mandate
 * screening check (LNXR-S02).
 *
 * This replaces a deliberate deviation from TTL-DES-LNX2-002 Section 6.8, which
 * put matches with new results first. Clients asked for one order everywhere,
 * so new evidence is now signalled by the card's NEW RESULT badge rather than by
 * its position. Ties fall back to last seen, newest first.
 */
export const orderMonitoringMatches = (matches: MonitoringMatch[]) =>
  orderByDecision(
    [...matches].sort((a, b) => toMillis(b.lastSeen) - toMillis(a.lastSeen)),
    (match) => match.status,
    (match) => match.body?.entityScore,
  );

/** "4 results · first 04 Mar 2026 · last 01 Aug 2026" */
export const runHistoryNote = (match: MonitoringMatch) => {
  const parts = [
    `${match.resultCount} result${match.resultCount === 1 ? "" : "s"}`,
  ];
  const first = formatShortDate(match.firstSeen);
  const last = formatShortDate(match.lastSeen);

  if (first) parts.push(`first ${first}`);
  if (last && last !== first) parts.push(`last ${last}`);

  return parts.join("  ·  ");
};

/**
 * Counts by decision, for the record summary. Drawn from the accumulated list.
 *
 * That list does not carry `exclude_reason`, so the excluded matches are passed
 * in by id, worked out from the run payloads, and left out of every count.
 */
export const decisionCounts = (
  accumulatedMatches: any[] = [],
  excludedIds: Set<number> = new Set(),
) =>
  accumulatedMatches.reduce(
    (counts, match) => {
      if (excludedIds.has(match?.id)) return counts;
      counts.total += 1;
      if (match?.status === "true_match") counts.trueMatch += 1;
      else if (match?.status === "false_positive") counts.falsePositive += 1;
      else counts.unknown += 1;
      return counts;
    },
    { total: 0, unknown: 0, falsePositive: 0, trueMatch: 0 },
  );

/**
 * A synthetic `records` array for ScreeningResultView.
 *
 * The component renders whatever record it is handed, so a scoped view is just a
 * different record built from the same parts. `informationUsed` comes from the
 * selected activity where there is one, and from the monitoring record itself
 * under All activity, because no single run is in scope there.
 */
export const buildScreeningRecord = (
  matches: MonitoringMatch[],
  informationUsed: any,
  bodyByMatchId: Map<number, any>,
) => {
  // excluded matches are rendered, in their own panel, but never counted
  const summary = matches.reduce(
    (counts, match) => {
      if (match.isExcluded) return counts;
      counts.total += 1;
      if (match.status === "true_match") counts.true_match += 1;
      else if (match.status === "false_positive") counts.false_positive += 1;
      else counts.unknown += 1;
      return counts;
    },
    { total: 0, unknown: 0, false_positive: 0, true_match: 0 },
  );

  return [
    {
      id: "monitoring-screening",
      information_used: informationUsed || {},
      check_results_summary: summary,
      matches: matches.map((match) => {
        const body = bodyByMatchId.get(match.id) ?? match.body;
        // the decision is the match's, never the run's, so it is reapplied over
        // whichever run's body is on screen
        return {
          ...body,
          status: match.status ?? body?.status,
          status_display: match.statusDisplay ?? body?.status_display,
        };
      }),
    },
  ];
};

/** Information Used under All activity, where no single run is in scope. */
export const informationUsedFromRecord = (monitoring: any) => {
  if (!monitoring) return {};
  const isEntity = monitoring?._type === "entity";

  return {
    name: isEntity
      ? monitoring?.company_name
      : [monitoring?.first_name, monitoring?.middle_name, monitoring?.last_name]
          .filter(Boolean)
          .join(" "),
    date_of_birth: isEntity ? undefined : monitoring?.date_of_birth,
    gender: isEntity ? undefined : monitoring?.gender,
    type: isEntity ? "Entity" : "Individual",
  };
};
